import { useState, useEffect } from 'react';
import useSWR, { mutate } from 'swr';

import { SESSION_TIMEOUT, FOCUS_THROTTLE_INTERVAL } from '@/config/appSettings';

import * as API from '@/lib/organizationApi';
import { IResponseError } from '@/lib/apiBase';
import { IOrganization } from '@/types';

const revalidate = (orgname: string) => {
  const key = ['organization', orgname];
  // console.log('revalidate > key', key);
  mutate(key, undefined, { revalidate: true });
};

const useOrganization = ({ orgName, initialData }: { orgName?: string; initialData?: IOrganization }) => {
  const [organization, setOrganization] = useState<IOrganization | undefined>(initialData);
  const [status, setStatus] = useState<'success' | 'error' | 'pending' | 'loading'>(
    !!initialData?.id ? 'success' : 'pending',
  );
  const [error, setError] = useState<IResponseError | undefined>();

  const fetchKey = orgName ? ['organization', orgName] : null;

  const {
    data,
    error: requestError,
    isLoading,
    isValidating,
  } = useSWR(fetchKey, () => API.getOrganization(orgName as string), {
    fallback: {
      organization: initialData,
      status: 'success',
      isLoading: false,
      statusCode: 200,
      status_message: 'OK',
    },
    keepPreviousData: true,
    revalidateOnFocus: false,
    revalidateOnMount: false,
    revalidateOnReconnect: false,
    focusThrottleInterval: FOCUS_THROTTLE_INTERVAL,
  });

  useEffect(() => {
    if (data) {
      // console.log('useOrganization > data', data);
      setOrganization(data?.organization);
      setStatus(data?.status);
      setError(data?.error as IResponseError);
    }
  }, [data]);

  const createSuccess = (response: API.IOrganizationResponse): void => {
    setError(undefined);
    const { organization } = response;
    // console.log('createSuccess > organization', organization);
    if (organization) {
      setOrganization(organization);
    }
  };

  const createFailed = (response: API.IOrganizationResponse): void => {
    setError(response.error as IResponseError);
    setOrganization(undefined);
  };

  const createOrganization = async ({ name }: API.ICreateParams): Promise<API.IOrganizationResponse> => {
    setStatus('loading');
    const response = await API.createOrganization({ name });

    // console.log('createOrganization > response', response);
    setStatus(response.status);

    if (response.status === 'success') {
      createSuccess(response);
    } else {
      createFailed(response);
    }
    return response;
  };

  const updateSuccess = (response: API.IOrganizationResponse): void => {
    setError(undefined);
    const { organization } = response;
    // console.log('updateSuccess > organization', organization);
    if (organization) {
      setOrganization(organization);
      revalidate(organization.name);
    }
  };

  const updateFailed = (response: API.IOrganizationResponse): void => {
    setError(response.error as IResponseError);
    setOrganization(undefined);
  };

  const updateOrganization = async ({
    orgname,
    name,
    display_name,
    description,
    image,
  }: API.IUpdateParams): Promise<string> => {
    setStatus('loading');
    const response = await API.updateOrganization({ orgname, name, display_name, description, image });

    setStatus(response.status);

    if (response.status === 'success') {
      updateSuccess(response);
    } else {
      updateFailed(response);
    }
    return response?.status;
  };

  return { organization, createOrganization, updateOrganization, status, error };
};

export default useOrganization;
